import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { createSignEmailSchema, createSignUpSchema } from '../schemas/user'
import { useLoginMutation, useRegisterMutation } from '../services/auth'
import { useGetCountriesQuery, useGetCountryCitiesQuery } from '../services/country'
import { authUser } from '../slices/auth'
import { SiteHeader } from '../components/SiteHeader'
import './Auth.css'

type LoginVals = { email: string; password: string }
type SignUpVals = {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  company?: string
  country: string
  city: string
}

export function LoginPage() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [login, { isLoading, error }] = useLoginMutation()
  const schema = useMemo(() => createSignEmailSchema(i18n.language), [i18n.language])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginVals>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: LoginVals) => {
    const res = await login({ email: data.email, password: data.password }).unwrap()
    dispatch(authUser({ accessToken: res.accessToken, user: res.user }))
    navigate('/app')
  }

  return (
    <div className="auth-page">
      <SiteHeader />
      <div className="auth">
        <div className="auth__panel">
          <div className="auth__hero">
            <p className="auth__brand">DriveOps</p>
            <h1>{t('Fleet tracking')}</h1>
            <p>Live map, history, and reports — same backend as the mobile app.</p>
          </div>
          <form className="auth__form" onSubmit={handleSubmit(onSubmit)}>
            <h2>{t('Authentification')}</h2>
            <label>
              {t('Email')}
              <input type="email" autoComplete="email" {...register('email')} />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </label>
            <label>
              {t('Mot de passe')}
              <input type="password" autoComplete="current-password" {...register('password')} />
              {errors.password && <span className="field-error">{errors.password.message}</span>}
            </label>
            {error && <p className="form-error">{t('Erreur de connexion')}</p>}
            <button className="btn btn--primary" type="submit" disabled={isLoading}>
              {isLoading ? t('Connexion…') : t("S’authentifier")}
            </button>
            <p className="auth__switch">
              {t('Pas encore de compte ?')} <Link to="/signup">{t('Créer un compte')}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export function SignUpPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [registerUser, { isLoading, error }] = useRegisterMutation()
  const schema = useMemo(() => createSignUpSchema(i18n.language), [i18n.language])
  const { data: countriesRes } = useGetCountriesQuery()
  const countries = countriesRes?.data ?? []
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpVals>({ resolver: zodResolver(schema) })
  const countryId = watch('country')
  const { data: cities = [] } = useGetCountryCitiesQuery(countryId, { skip: !countryId })

  const onSubmit = async (data: SignUpVals) => {
    const { confirmPassword, ...body } = data
    void confirmPassword
    await registerUser(body).unwrap()
    navigate('/login')
  }

  return (
    <div className="auth-page">
      <SiteHeader />
      <div className="auth">
        <div className="auth__panel auth__panel--wide">
          <div className="auth__hero">
            <p className="auth__brand">DriveOps</p>
            <h1>{t('Inscription')}</h1>
            <p>Create your account to track vehicles on web and mobile.</p>
          </div>
          <form className="auth__form" onSubmit={handleSubmit(onSubmit)}>
            <div className="auth__grid">
              <label>
                {t('Prénom')}
                <input {...register('firstName')} />
                {errors.firstName && <span className="field-error">{errors.firstName.message}</span>}
              </label>
              <label>
                {t('Nom')}
                <input {...register('lastName')} />
                {errors.lastName && <span className="field-error">{errors.lastName.message}</span>}
              </label>
              <label className="span-2">
                {t('Email')}
                <input type="email" {...register('email')} />
                {errors.email && <span className="field-error">{errors.email.message}</span>}
              </label>
              <label>
                {t('Mot de passe')}
                <input type="password" {...register('password')} />
                {errors.password && <span className="field-error">{errors.password.message}</span>}
              </label>
              <label>
                {t('Confirmer le mot de passe')}
                <input type="password" {...register('confirmPassword')} />
                {errors.confirmPassword && (
                  <span className="field-error">{errors.confirmPassword.message}</span>
                )}
              </label>
              <label className="span-2">
                {t('Entreprise')}
                <input {...register('company')} />
              </label>
              <label>
                {t('Pays')}
                <select {...register('country')}>
                  <option value="">—</option>
                  {countries.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.country && <span className="field-error">{errors.country.message}</span>}
              </label>
              <label>
                {t('Ville')}
                <select {...register('city')} disabled={!countryId}>
                  <option value="">—</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
                {errors.city && <span className="field-error">{errors.city.message}</span>}
              </label>
            </div>
            {error && <p className="form-error">{t('Erreur de connexion')}</p>}
            <button className="btn btn--primary" type="submit" disabled={isLoading}>
              {isLoading ? t('Chargement…') : t("S’inscrire")}
            </button>
            <p className="auth__switch">
              {t('Déjà un compte ?')} <Link to="/login">{t('Se connecter')}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
